import React, { useActionState, useEffect, useState } from "react";
import * as UserActions from "@/actions/user.actions";
import { Button } from "../Button";
import { BackIcon } from "../Icons";
import { VerifyOTP } from "./VerifyOTP";
import { SendOTP } from "./SendOTP";
import { QuickLinks } from "./QuickLinks";
import { LoginWith } from "./LoginWith";
import { EventNamesEnum, LoginPageStateEnum } from "@/constants/constants";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { useDeviceType } from "@/hooks/useDeviceType";
import { useRouter } from "next/navigation";
import { useRef } from "react";

export const LoginForm = ({
  setIsInputFocused,
  isLoginDrawer = false,
  logTitle,
  logSubTitle,
}: {
  setIsInputFocused?: (isInputFocused: boolean) => void;
  isLoginDrawer?: boolean;
  logTitle?: string;
  logSubTitle?: string;
}) => {
  const [sendOTPState, sendOTPFormAction] = useActionState(
    // @ts-ignore
    UserActions.sendOTPAction,
    undefined,
  );
  const { trackEvent } = useTrackingAnalytics();
  const [currentLoginPageState, setCurrentLoginPageState] = useState<
    LoginPageStateEnum.SEND_OTP | LoginPageStateEnum.VERIFY_OTP
  >(LoginPageStateEnum.SEND_OTP);
  const { isMobile } = useDeviceType();
  const router = useRouter();
  const hiddenKeyboardInputRef = useRef<HTMLInputElement>(null);

  const resetLoginPageState = () => {
    setCurrentLoginPageState(LoginPageStateEnum.SEND_OTP);
  };

  const toggleLoginPageState = () => {
    setCurrentLoginPageState((currentLoginPageState) => {
      if (currentLoginPageState === LoginPageStateEnum.SEND_OTP) {
        // Keep keypad open on iOS by focusing a temporary numeric field
        if (isMobile) {
          hiddenKeyboardInputRef.current?.focus();
          setIsInputFocused?.(true);
        }
        return LoginPageStateEnum.VERIFY_OTP;
      }
      return LoginPageStateEnum.SEND_OTP;
    });
  };
  useEffect(() => {
    if (sendOTPState?.mobileNumber && sendOTPState?.userId) {
      const urlParams = new URLSearchParams(window.location.search);
      const redirectionAfterLoginTo = urlParams.get("redirectionAfterLoginTo");
      const sourceSplit = redirectionAfterLoginTo?.split("/") ?? [];
      const sourcePage =
        sourceSplit.length > 0 ? sourceSplit[sourceSplit.length - 1] : "";

      trackEvent({
        eventName: EventNamesEnum.LOGIN_INITIATED,
        properties: {
          user_id: sendOTPState?.userId,
          mobileNumber: sendOTPState?.mobileNumber,
          login_method: "phone",
          login_stage: "otp_requested", //ph_typin
          source_page_name: sourcePage,
          country_code: sendOTPState?.countryCode,
        },
        sendTo: [],
      });
    }
  }, [sendOTPState?.userId]);

  const handleInputFocus = () => {
    setIsInputFocused?.(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused?.(false);
  };

  const handleInputFocusWithScroll = () => {
    setIsInputFocused?.(true);
    if (isMobile) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleBack = async () => {
    router.back();
  };

  // When switching to verifyOTP, immediately focus the first OTP input to keep the keypad open
  useEffect(() => {
    if (
      currentLoginPageState === LoginPageStateEnum.VERIFY_OTP &&
      sendOTPState?.userId &&
      typeof window !== "undefined"
    ) {
      const focusFirstOtp = () => {
        const el = document.getElementById(
          "otp-digit-1",
        ) as HTMLInputElement | null;
        if (el) {
          el.focus();
          setIsInputFocused?.(true);
        }
      };

      // Try immediately, then on next frame, then after a short delay
      focusFirstOtp();
      requestAnimationFrame(focusFirstOtp);
      setTimeout(focusFirstOtp, 50);
    }
  }, [currentLoginPageState, sendOTPState?.userId]);

  return (
    <div
      className={`flex flex-1 flex-col items-center justify-end  ${isLoginDrawer ? "sm:w-[400px] md:w-fit md:!p-0 md:!px-2" : ""}`}
    >
      {/* Hidden input to keep the keyboard open during transition */}
      <input
        ref={hiddenKeyboardInputRef}
        type="tel"
        inputMode="numeric"
        aria-hidden="true"
        className="absolute h-[1px] w-[1px] opacity-0"
        tabIndex={-1}
      />
      {sendOTPState?.userId &&
      currentLoginPageState === LoginPageStateEnum.VERIFY_OTP ? (
        <>
          <div className=" w-full px-4">
            <Button
              className="mr-4"
              noBg={true}
              icon={<BackIcon />}
              dataTestId="header-left-back-btn"
              onClick={toggleLoginPageState}
            />
          </div>
          <VerifyOTP
            sendOTPState={sendOTPState}
            sendOTPFormAction={sendOTPFormAction}
            resetLoginPageState={resetLoginPageState}
            onInputFocus={handleInputFocus}
            onInputBlur={handleInputBlur}
          />
        </>
      ) : (
        <SendOTP
          sendOTPState={sendOTPState}
          sendOTPFormAction={sendOTPFormAction}
          toggleLoginPageState={toggleLoginPageState}
          onInputFocus={handleInputFocusWithScroll}
          onInputBlur={handleInputBlur}
          logTitle={logTitle}
          logSubTitle={logSubTitle}
        />
      )}
      <LoginWith />
      <div className="md:hidden">
        <QuickLinks />
      </div>
    </div>
  );
};
